import { StateCreator } from 'zustand';
import getConfig from 'next/config';
import builder from '@builder.io/react';
import * as appointmentService from '@/services/appointment.service';
import { IS_DEV } from '@/utils/env';
import * as promoService from '@/services/promo.service';
import { filterStoresWithinDistance } from '@/utils/utils';
import { ShowroomStatusOptions } from '@/components/BuilderIO/common/CommonFields';
import { IBuilder, IPromotion } from '../types/IBuilder';

const createBuilderSlice: StateCreator<IBuilder> = (set, get) => ({
  mobileNavLinks: [],
  builderShowrooms: [],
  filteredShowrooms: [],
  filteredAppointmentModalShowrooms: [],
  builderData: null,
  loadingFirstTime: true,
  setMobileNavLinks: mobileNavLinks => {
    set(state => ({ ...state, mobileNavLinks }));
  },
  getPromotions: async () => {
    const promo = (await promoService.getPromotions()) as IPromotion;
    set(state => ({ builderData: { ...state.builderData, promo } }));
  },
  setBuilderData(props) {
    // Maybe this needs a better way to separate the data
    const promo = Array.isArray(props) ? props.find(item => item && (item.topPurpleBar || item.topNav)) : undefined;
    const headerBanner =
      props && Array.isArray(props)
        ? props.flatMap(innerArray => (Array.isArray(innerArray) ? innerArray : [])).find(item => item && item.blocks)
        : undefined;

    set(state => ({
      builderData: { ...state.builderData, promo, headerBanner },
    }));
  },
  setBuilderShowrooms: async initialShowrooms => {
    let showrooms;

    if (initialShowrooms) {
      showrooms = initialShowrooms;
    } else {
      const { NEXT_PUBLIC_BUILDER_API_KEY: apiKey } = getConfig().publicRuntimeConfig;
      showrooms = (await builder
        .getAll('showrooms', { apiKey, limit: 100 })
        .then(d => d.map(({ data }) => data))) as any;

      try {
        const { data: cities } = await appointmentService.getCitiesData(IS_DEV);

        const showroomsWithCityId = showrooms.map(showroom => {
          const city = cities?.find(c => c.name?.trim() === showroom.setsterLocationId?.trim());

          return {
            ...showroom,
            id: city?.id ?? null,
            timezoneId: city?.['timezone_id'] ?? null,
            timezoneAbreviation: city?.timezone.abreviation ?? null,
          };
        });

        showrooms = showroomsWithCityId;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }

    const index = showrooms.findIndex(location => location.displayName1 === 'Virtual Appointment');
    const virtualAppointment = index !== -1 ? showrooms[index] : undefined;

    const updatedShowrooms =
      index !== -1 ? [...showrooms.slice(0, index), ...showrooms.slice(index + 1)] : [...showrooms];

    if (virtualAppointment) {
      updatedShowrooms.push(virtualAppointment);
    }

    set(state => ({ ...state, builderShowrooms: updatedShowrooms }));

    return new Promise<void>(resolve => {
      resolve();
    });
  },
  setShowroomCityId: async (showroom, type = 'setster_id') => {
    try {
      const { data: cities } = await appointmentService.getCitiesData(IS_DEV);

      const showroomCity =
        cities?.find(
          c =>
            (type === 'setster_id' && c.name?.trim() === showroom.setsterLocationId?.trim()) ||
            (type === 'name' && c.name?.trim() === showroom.displayName1?.trim())
        ) ||
        (type === 'setster_id' && cities?.find(c => c.zip === showroom.zipCode));

      return {
        id: showroomCity?.id,
        timezoneId: showroomCity?.['timezone_id'],
        timezoneAbreviation: showroomCity?.timezone.abreviation,
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  },
  setShowroomsCitiesIds: async showrooms => {
    try {
      const { data: cities } = await appointmentService.getCitiesData(IS_DEV);

      const showroomsWithCityId = showrooms.map(showroom => {
        const city = cities?.find(c => c.name?.trim() === showroom.setsterLocationId?.trim());
        return {
          ...showroom,
          id: city?.id,
          timezoneId: city?.['timezone_id'],
          timezoneAbreviation: city?.timezone.abreviation,
        };
      });

      return showroomsWithCityId;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
    return showrooms;
  },
  getShowroomCityId: async cityName => {
    try {
      const { data: cities } = await appointmentService.getCitiesData(IS_DEV);

      return cities?.find(city => city.name.trim() === cityName.trim())?.id;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  },
  setNearShowrooms: async (latitude, longitude, type = '', searchInput = '') => {
    let showrooms = get().builderShowrooms;

    if (showrooms.length <= 0) {
      await get().setBuilderShowrooms(null);
      showrooms = get().builderShowrooms;
    }

    const index = showrooms.findIndex(location => location.displayName1 === 'Virtual Appointment');
    const virtualAppointment = index !== -1 ? showrooms[index] : undefined;

    let updatedShowrooms =
      index !== -1 ? [...showrooms.slice(0, index), ...showrooms.slice(index + 1)] : [...showrooms];

    if (latitude === 38.7945952 && longitude === -106.5348379) {
      updatedShowrooms = [];
    } else if (type) {
      updatedShowrooms = showrooms.filter(show => show.state && show.state.trim() === type);
    } else if (latitude && longitude) {
      updatedShowrooms = filterStoresWithinDistance({ latitude, longitude }, updatedShowrooms, 250, searchInput);
    }

    if (virtualAppointment && updatedShowrooms.length > 0) {
      updatedShowrooms.push(virtualAppointment);
    }

    const formatedShowrooms = await get().setShowroomsCitiesIds(updatedShowrooms);

    set(state => ({ ...state, filteredShowrooms: formatedShowrooms, loadingFirstTime: false }));

    return formatedShowrooms;
  },
  setNearAppointmentModalShowrooms: async (latitude, longitude, type = '', searchInput = '') => {
    let showrooms = get().builderShowrooms;

    if (showrooms.length <= 0) {
      await get().setBuilderShowrooms(null);
      showrooms = get().builderShowrooms;
    }

    const index = showrooms.findIndex(location => location.displayName1 === 'Virtual Appointment');
    const virtualAppointment = index !== -1 ? showrooms[index] : undefined;

    let updatedShowrooms =
      index !== -1 ? [...showrooms.slice(0, index), ...showrooms.slice(index + 1)] : [...showrooms];

    if (latitude === 38.7945952 && longitude === -106.5348379) {
      updatedShowrooms = [];
    } else if (type) {
      updatedShowrooms = showrooms.filter(show => show.state === type);
    } else if (latitude && longitude) {
      updatedShowrooms = filterStoresWithinDistance({ latitude, longitude }, updatedShowrooms, 250, searchInput);
    }

    if (virtualAppointment && updatedShowrooms.length > 0) {
      updatedShowrooms.push(virtualAppointment);
    }

    const formatedShowrooms = await get().setShowroomsCitiesIds(updatedShowrooms);

    const onlyActiveShowrooms = formatedShowrooms?.filter(
      showroom => showroom.status !== ShowroomStatusOptions.ComingSoon
    );

    set(state => ({
      ...state,
      filteredAppointmentModalShowrooms: onlyActiveShowrooms,
      loadingFirstTime: false,
    }));

    return onlyActiveShowrooms;
  },
});

export default createBuilderSlice;
